/* ==========================================================================
   Outlet4IT — minimal, accessible interactions for the homepage preview
   - <o4-header>  : sticky shadow on scroll (IntersectionObserver sentinel)
   - <o4-drawer>  : mobile navigation drawer (open/close, focus trap, Escape)
   No dependencies. ES6. ~1.5 KB.
   ========================================================================== */

(function () {
  'use strict';

  /* ── Sticky header shadow ─────────────────────────────────────────────── */
  class O4Header extends HTMLElement {
    connectedCallback() {
      var header = this;
      /* The sticky/moving unit is the whole header group (announcement + header). */
      var group = this.closest('.shopify-section-group-header-group') || this;
      this._group = group;

      /* Scroll driver (rAF-throttled): shrink once scrolled (is-stuck), and
         hide on scroll down / reveal on scroll up (is-hidden). */
      this._lastY = window.pageYOffset;
      this._ticking = false;
      this._update = function () {
        var y = window.pageYOffset;
        var delta = y - header._lastY;

        group.classList.toggle('is-stuck', y > 4);

        if (y <= group.offsetHeight) {
          group.classList.remove('is-hidden');    /* always show near the top */
        } else if (delta > 6) {
          group.classList.add('is-hidden');        /* scrolling down */
        } else if (delta < -6) {
          group.classList.remove('is-hidden');     /* scrolling up */
        }
        header._lastY = y;
        header._ticking = false;
      };
      this._onScroll = function () {
        if (header._ticking) return;
        header._ticking = true;
        window.requestAnimationFrame(header._update);
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._update(); /* set initial state */

      /* Reveal when a header element receives keyboard focus (accessibility). */
      this._onFocusIn = function () { group.classList.remove('is-hidden'); };
      this.addEventListener('focusin', this._onFocusIn);
    }

    disconnectedCallback() {
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onFocusIn) this.removeEventListener('focusin', this._onFocusIn);
    }
  }
  customElements.define('o4-header', O4Header);

  /* ── Mobile drawer ────────────────────────────────────────────────────── */
  class O4Drawer extends HTMLElement {
    connectedCallback() {
      this._panel   = this.querySelector('.o4-drawer__panel');
      this._toggle  = document.querySelector('[data-o4-drawer-open="' + this.id + '"]');
      this._lastFocus = null;

      this._onKeydown = this._onKeydown.bind(this);

      if (this._toggle) {
        this._toggle.addEventListener('click', this.open.bind(this));
      }
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-o4-drawer-close]')) this.close();
      }.bind(this));
    }

    open() {
      this._lastFocus = document.activeElement;
      this.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      if (this._toggle) this._toggle.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this._onKeydown);
      var first = this._panel.querySelector('a, button');
      if (first) first.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      if (this._toggle) this._toggle.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this._onKeydown);
      if (this._lastFocus) this._lastFocus.focus();
    }

    _onKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key !== 'Tab') return;
      /* Simple focus trap */
      var focusables = this._panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focusables.length) return;
      var first = focusables[0];
      var last  = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
  customElements.define('o4-drawer', O4Drawer);

  /* ── Bottom-nav search toggle ─────────────────────────────────────────── */
  /* The mobile search bar is collapsed by default; the "Suche" tab in the
     bottom navigation reveals it and focuses the field. */
  var searchToggle = document.querySelector('[data-o4-search-toggle]');
  var searchBar = document.querySelector('[data-o4-search-bar]');
  if (searchToggle && searchBar) {
    searchToggle.addEventListener('click', function () {
      var open = searchBar.classList.toggle('is-open');
      searchToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        var input = searchBar.querySelector('input');
        if (input) window.requestAnimationFrame(function () { input.focus(); });
      }
    });
    searchBar.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        searchBar.classList.remove('is-open');
        searchToggle.setAttribute('aria-expanded', 'false');
      }
    });
  }
})();
